import React from 'react';
import { MapPin, Bookmark, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const OfferCardHorizontal = ({ offer, onSave, isSaved = false, source = 'other' }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/offer/${offer.id}?source=${source}`);
  };

  const handleSaveClick = (e) => {
    e.stopPropagation();
    if (onSave) {
      onSave(offer.id);
    }
  };

  return (
    <div 
      onClick={handleCardClick}
      className="flex-shrink-0 w-64 bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
      data-testid="offer-card-horizontal"
    >
      {/* Square Image/Video with Gradient Overlay */}
      <div className="relative h-64 bg-gradient-to-br from-green-400 via-yellow-400 to-orange-400">
        {offer.video_url && !offer.image_url ? (
          <video 
            src={offer.video_url}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
            poster={offer.video_thumbnail || `${offer.video_url}#t=0.1`}
            onLoadedData={(e) => {
              // Seek to first frame to show thumbnail
              e.target.currentTime = 0.1;
            }}
          />
        ) : (
          <img 
            src={offer.image_url || offer.video_thumbnail || '/image-not-available.png'}
            alt={offer.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = '/image-not-available.png';
            }}
          />
        )}
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

        {/* Video Play Icon */}
        {offer.video_url && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black/50 rounded-full p-3">
              <Play className="w-8 h-8 text-white fill-white" />
            </div>
          </div>
        )}
        
        {/* Discount Badge */}
        {offer.discount_percentage && (
          <div className="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full font-bold text-sm shadow-lg">
            {offer.discount_percentage}% OFF
          </div>
        )}
        
        {/* Save Button */}
        <button
          onClick={handleSaveClick}
          className={`absolute top-3 right-3 rounded-full p-2 shadow-lg transition-all ${
            isSaved 
              ? 'bg-green-500 hover:bg-green-600' 
              : 'bg-white hover:bg-gray-100'
          }`}
          data-testid="save-button"
        >
          <Bookmark 
            size={18} 
            className={isSaved ? 'text-white' : 'text-gray-700'}
            fill={isSaved ? 'white' : 'none'}
          />
        </button>

        {/* Title Overlay at Bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="font-bold text-white text-lg mb-1 line-clamp-2">
            {offer.title}
          </h3>
          <p className="text-white/90 text-sm font-medium">{offer.merchant_name}</p>
        </div>

        {/* Golden Pass Badge — bottom-right of image (avoids discount top-left & save top-right) */}
        {offer.has_golden_passes && (
          <div
            className="absolute bottom-2 right-2 z-20 flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-[11px] font-bold rounded-full shadow-lg ring-1 ring-white/60"
            data-testid="golden-pass-badge"
          >
            <span>🎟</span> Golden Pass
          </div>
        )}
      </div>

      {/* Bottom Info */}
      <div className="p-3 bg-white">
        <div className="flex items-center text-xs text-gray-500 mb-2">
          <MapPin size={12} className="mr-1" />
          <span className="line-clamp-1">{offer.distance ? `${offer.distance} km` : offer.address}</span>
        </div>
        <div className="text-xs text-green-600 font-medium">
          Valid: {new Date(offer.valid_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </div>
      </div>
    </div>
  );
};

export default OfferCardHorizontal;
